import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography, Paper, Divider } from '@mui/material';

interface TermsScreenProps {
  onAgree: () => void;
}

const TermsScreen: React.FC<TermsScreenProps> = ({ onAgree }) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper
        sx={{
          maxWidth: 800,
          width: '100%',
          p: 4,
          bgcolor: '#1a1a1a',
          color: 'white'
        }}
      >
        <Typography variant="h4" align="center" gutterBottom sx={{ color: '#10B981', mb: 4 }}>
          利用規約とプライバシーポリシー
        </Typography>

        <Paper
          sx={{
            height: 500,
            overflow: 'auto',
            p: 3,
            mb: 3,
            bgcolor: '#0a0a0a',
            border: '1px solid #333'
          }}
        >
          <TermsOfService />
          <Divider sx={{ my: 4, borderColor: '#333' }} />
          <PrivacyPolicy />
        </Paper>

        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              sx={{
                color: '#888',
                '&.Mui-checked': {
                  color: '#10B981'
                }
              }}
            />
          }
          label={
            <Typography sx={{ color: 'white' }}>
              上記の利用規約とプライバシーポリシーに同意します
            </Typography>
          }
          sx={{ mb: 3 }}
        />

        <Button
          fullWidth
          variant="contained"
          disabled={!agreed}
          onClick={onAgree}
          sx={{
            bgcolor: agreed ? '#10B981' : '#333',
            color: 'white',
            py: 1.5,
            fontSize: 16,
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: agreed ? '#0ea370' : '#333'
            },
            '&:disabled': {
              bgcolor: '#333',
              color: '#666'
            }
          }}
        >
          同意して次へ
        </Button>
      </Paper>
    </Box>
  );
};

const TermsOfService = () => (
  <Box sx={{ color: '#ccc' }}>
    <Typography variant="h5" gutterBottom sx={{ color: '#10B981', fontWeight: 'bold' }}>
      利用規約
    </Typography>
    <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
      <strong>最終更新日：2025年10月15日</strong>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第1条（規約への同意）
    </Typography>
    <Typography variant="body2" paragraph>
      本アプリをご利用いただくことで、本利用規約に同意したものとみなします。
      同意いただけない場合は、本アプリをご利用いただけません。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第2条（サービス内容）
    </Typography>
    <Typography variant="body2" paragraph>
      本アプリは、収支管理サービスを提供します。サービスの詳細は別途定めるものとします。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第3条（広告表示について）
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ol style={{ paddingLeft: 20, margin: 0 }}>
        <li>本アプリでは、第三者配信の広告サービス（Google AdSense等）を利用しています</li>
        <li>広告配信のため、Cookieや広告識別子を使用する場合があります</li>
        <li>広告をクリックした場合、外部サイトへ移動します</li>
        <li>広告の内容について当方は責任を負いません</li>
      </ol>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第4条（免責事項）
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ol style={{ paddingLeft: 20, margin: 0 }}>
        <li>本アプリは「現状有姿」で提供され、いかなる保証もいたしません</li>
        <li>本アプリの利用により生じた損害について、当方は一切の責任を負いません</li>
        <li>サービスの中断、終了、変更により生じた損害について責任を負いません</li>
        <li>表示される情報の正確性、完全性を保証しません</li>
        <li>外部リンク先のウェブサイトの内容について責任を負いません</li>
      </ol>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第5条（禁止事項）
    </Typography>
    <Typography variant="body2" paragraph>
      以下の行為を禁止します：
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ol style={{ paddingLeft: 20, margin: 0 }}>
        <li>法令に違反する行為</li>
        <li>他のユーザーや第三者の権利を侵害する行為</li>
        <li>本アプリの運営を妨害する行為</li>
        <li>不正アクセス、リバースエンジニアリング</li>
        <li>商用利用（事前の許可なく）</li>
        <li>虚偽の情報を登録する行為</li>
      </ol>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第6条（アカウント管理）
    </Typography>
    <Typography variant="body2" paragraph>
      ユーザーは自己の責任でアカウント情報を管理してください。
      アカウントの不正使用による損害は利用者の責任となります。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第7条（知的財産権）
    </Typography>
    <Typography variant="body2" paragraph>
      本アプリに関する著作権、商標権などの知的財産権は当方に帰属します。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第8条（プライバシー）
    </Typography>
    <Typography variant="body2" paragraph>
      個人情報の取り扱いについては、別途プライバシーポリシーをご確認ください。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第9条（サービスの変更・終了）
    </Typography>
    <Typography variant="body2" paragraph>
      当方は、予告なく本アプリの内容を変更、または提供を終了できるものとします。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第10条（準拠法・管轄裁判所）
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ol style={{ paddingLeft: 20, margin: 0 }}>
        <li>本規約は日本法に準拠します</li>
        <li>紛争が生じた場合、東京地方裁判所を専属的合意管轄裁判所とします</li>
      </ol>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第11条（規約の変更）
    </Typography>
    <Typography variant="body2" paragraph>
      当方は本規約を随時変更できるものとします。
      変更後の規約は、本アプリ上で公開した時点で効力を生じます。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      第12条（お問い合わせ）
    </Typography>
    <Typography variant="body2" paragraph>
      本規約に関するお問い合わせ：rakugajelab@gmail.com
    </Typography>
  </Box>
);

const PrivacyPolicy = () => (
  <Box sx={{ color: '#ccc' }}>
    <Typography variant="h5" gutterBottom sx={{ color: '#10B981', fontWeight: 'bold' }}>
      プライバシーポリシー
    </Typography>
    <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
      <strong>最終更新日：2025年10月15日</strong>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      1. 収集する情報
    </Typography>
    <Typography variant="body2" paragraph>
      当方は、以下の情報を収集します：
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ul style={{ paddingLeft: 20, margin: 0 }}>
        <li>アカウント情報（メールアドレス、氏名等）</li>
        <li>利用状況データ（アクセス日時、利用機能等）</li>
        <li>Cookie、広告識別子</li>
        <li>収支データ</li>
        <li>IPアドレス、ブラウザ情報</li>
      </ul>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      2. 情報の利用目的
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ul style={{ paddingLeft: 20, margin: 0 }}>
        <li>サービスの提供・改善</li>
        <li>広告配信（Google AdSense等）</li>
        <li>お問い合わせ対応</li>
        <li>利用規約違反の調査</li>
        <li>統計データの作成</li>
      </ul>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      3. 第三者提供
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ul style={{ paddingLeft: 20, margin: 0 }}>
        <li>広告配信事業者（Google AdSense、Google Analytics等）に情報を提供します</li>
        <li>法令に基づく場合を除き、同意なく第三者に提供しません</li>
        <li>サービス提供に必要な範囲で業務委託先に提供する場合があります</li>
      </ul>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      4. Cookie・トラッキング技術
    </Typography>
    <Typography variant="body2" paragraph>
      本アプリでは、以下の目的でCookieを使用します：
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ul style={{ paddingLeft: 20, margin: 0 }}>
        <li>広告配信の最適化</li>
        <li>アクセス解析</li>
        <li>ユーザー体験の向上</li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>
      Cookieはブラウザ設定で無効化できますが、一部機能が制限される場合があります。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      5. Google AdSenseについて
    </Typography>
    <Typography variant="body2" paragraph>
      本アプリはGoogle AdSenseを使用しています。
      Googleは広告配信のためにCookieを使用し、過去のアクセス情報に基づいて広告を配信します。
    </Typography>
    <Typography variant="body2" paragraph>
      Google広告Cookieの使用を無効にするには、
      <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" style={{ color: '#10B981' }}>
        Googleの広告設定
      </a>
      から設定できます。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      6. データの保管期間
    </Typography>
    <Typography variant="body2" paragraph>
      収集した情報は、利用目的を達成するために必要な期間保管します。
      アカウント削除後は速やかに削除します。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      7. セキュリティ
    </Typography>
    <Typography variant="body2" paragraph>
      適切な安全管理措置を講じますが、完全な安全性は保証できません。
      ユーザーご自身でもパスワード管理等のセキュリティ対策を行ってください。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      8. ユーザーの権利
    </Typography>
    <Typography variant="body2" paragraph>
      ユーザーは、自己の個人情報について以下の権利を有します：
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      <ul style={{ paddingLeft: 20, margin: 0 }}>
        <li>開示請求</li>
        <li>訂正請求</li>
        <li>削除請求</li>
        <li>利用停止請求</li>
      </ul>
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      9. プライバシーポリシーの変更
    </Typography>
    <Typography variant="body2" paragraph>
      当方は本ポリシーを随時変更できるものとします。
      変更後のポリシーは、本アプリ上で公開した時点で効力を生じます。
    </Typography>

    <Typography variant="subtitle1" sx={{ mt: 3, color: '#10B981', fontWeight: 'bold' }}>
      10. お問い合わせ
    </Typography>
    <Typography variant="body2" paragraph>
      プライバシーポリシーに関するお問い合わせ：info@example.com
    </Typography>
  </Box>
);

export default TermsScreen;